import React, { useState, useEffect, useCallback } from 'react';
import apiFetch from '../../../../utils/api';
import Preloader from '../../../../components/Preloader/Preloader';
import './AdminStyles.css';

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function fmtDuration(secs) {
  if (secs == null) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtStart(secs) {
  if (secs == null) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const AdminAdWatchLog = () => {
  const [date, setDate]             = useState(todayStr());
  const [records, setRecords]       = useState([]);
  const [summary, setSummary]       = useState({ total_completed: 0, total_earned: 0 });
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  // Confirm-delete state
  const [confirmRow, setConfirmRow] = useState(null); // record object | null
  const [deleting, setDeleting]     = useState(false);

  // Feedback flash
  const [flash, setFlash]           = useState({ text: '', ok: true });
  const showFlash = (text, ok = true) => {
    setFlash({ text, ok });
    setTimeout(() => setFlash({ text: '', ok: true }), 5000);
  };

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/admin/ad-watch-log?date=${date}`);
      const d   = await res.json();
      if (!d.success) { setError(d.message || 'Failed to load.'); setLoading(false); return; }
      setRecords(d.records || []);
      setSummary({ total_completed: d.total_completed, total_earned: d.total_earned });
    } catch {
      setError('Network error — could not load log.');
    }
    setLoading(false);
  }, [date]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleDelete = async () => {
    if (!confirmRow) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/admin/ad-watch-log/${confirmRow.state_id}/delete`, { method: 'DELETE' });
      const d   = await res.json();
      if (d.success) {
        showFlash(d.message);
        setRecords(prev => prev.filter(r => r.state_id !== confirmRow.state_id));
        setSummary(prev => ({
          total_completed: prev.total_completed - 1,
          total_earned: prev.total_earned - confirmRow.earned,
        }));
      } else {
        showFlash(d.message || 'Could not delete record.', false);
      }
    } catch {
      showFlash('Network error.', false);
    }
    setDeleting(false);
    setConfirmRow(null);
  };

  return (
    <div className="admin-page">
      <div className="admin-breadcrumb">🏠 / Admin / Ad Watch Log</div>
      <h2 className="admin-title">Ad Watch Log</h2>

      {/* Flash */}
      {flash.text && (
        <div className="admin-card" style={{
          background: flash.ok ? 'var(--awp-success-soft)' : 'var(--awp-danger-soft)',
          border: `1px solid ${flash.ok ? 'var(--awp-success-border)' : 'var(--awp-danger-border)'}`,
          marginBottom: 16,
        }}>
          <div className="admin-card-body" style={{ padding: '10px 16px', color: flash.ok ? 'var(--awp-success)' : 'var(--awp-danger)', fontSize: 14 }}>
            {flash.text}
          </div>
        </div>
      )}

      {/* Date picker + refresh */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="admin-card-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--awp-text-muted)' }}>Date</label>
            <input
              type="date"
              value={date}
              max={todayStr()}
              onChange={e => setDate(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--awp-border)', fontSize: 14, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
            />
            <button
              className="admin-btn admin-btn--edit"
              onClick={fetchLog}
              disabled={loading}
              style={{ fontSize: 13, padding: '8px 18px' }}
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button
              className="admin-btn admin-btn--edit"
              onClick={() => { setDate(todayStr()); }}
              style={{ fontSize: 13, padding: '8px 14px', opacity: date === todayStr() ? 0.5 : 1 }}
              disabled={date === todayStr()}
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px', background: 'var(--awp-info-soft)', borderRadius: 10, padding: '16px 20px', border: '1px solid var(--awp-info-border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--awp-info)', marginBottom: 4 }}>Completions</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--awp-text-strong)' }}>{summary.total_completed}</div>
          <div style={{ fontSize: 12, color: 'var(--awp-text-muted)', marginTop: 2 }}>users watched today</div>
        </div>
        <div style={{ flex: '1 1 180px', background: 'var(--awp-success-soft)', borderRadius: 10, padding: '16px 20px', border: '1px solid var(--awp-success-border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--awp-success)', marginBottom: 4 }}>Total Earned</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--awp-text-strong)' }}>Rs. {summary.total_earned.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: 'var(--awp-text-muted)', marginTop: 2 }}>credited to users</div>
        </div>
      </div>

      {/* Log table */}
      <div className="admin-card">
        <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Watch Records — {date}</span>
          <span style={{ fontSize: 12, color: 'var(--awp-text-muted)', fontWeight: 400 }}>{records.length} record{records.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="admin-card-body admin-table-wrap">
          {loading ? (
            <Preloader />
          ) : error ? (
            <div className="admin-empty" style={{ color: 'var(--awp-danger)' }}>{error}</div>
          ) : records.length === 0 ? (
            <div className="admin-empty">No completed ad watches on {date}.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>Video</th>
                  <th style={{ width: 90 }}>Started</th>
                  <th style={{ width: 90 }}>Completed</th>
                  <th style={{ width: 80 }}>Duration</th>
                  <th style={{ width: 80 }}>Segment</th>
                  <th style={{ width: 80 }}>Earned</th>
                  <th style={{ width: 70 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => (
                  <tr key={r.state_id}>
                    <td style={{ fontSize: 12, color: 'var(--awp-text-subtle)' }}>{idx + 1}</td>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>{r.username}</td>
                    <td style={{ fontSize: 12, maxWidth: 240 }}>
                      <span title={r.video_title} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.video_title}
                      </span>
                      {r.video_id && (
                        <span style={{ fontSize: 11, color: 'var(--awp-text-subtle)' }}>{r.video_id}</span>
                      )}
                    </td>
                    <td style={{ fontSize: 13 }}>{r.watch_started_at || '—'}</td>
                    <td style={{ fontSize: 13 }}>{r.watch_completed_at || '—'}</td>
                    <td style={{ fontSize: 13 }}>{fmtDuration(r.watch_duration_seconds)}</td>
                    <td style={{ fontSize: 13 }}>
                      <span title="Segment start position in video">{fmtStart(r.start_seconds)}</span>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                        background: 'var(--awp-success-soft)', color: 'var(--awp-success)', fontSize: 12, fontWeight: 700,
                      }}>
                        Rs. {r.earned}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => setConfirmRow(r)}
                        title="Delete this record and reverse all related data"
                        style={{
                          background: 'var(--awp-danger-soft)', border: '1px solid var(--awp-danger-border)', color: 'var(--awp-danger)',
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmRow && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--awp-surface)', borderRadius: 12, padding: '28px 32px',
            maxWidth: 440, width: '90%', boxShadow: 'var(--awp-shadow-lg)',
            border: '1px solid var(--awp-border)'
          }}>
            <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, color: 'var(--awp-text-strong)', textAlign: 'center' }}>
              Delete watch record for <strong>{confirmRow.username}</strong>?
            </h3>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--awp-text-muted)', textAlign: 'center' }}>
              This will permanently remove:
            </p>
            <ul style={{ margin: '0 0 20px', paddingLeft: 20, fontSize: 13, color: 'var(--awp-text)' }}>
              <li>The completion status for {date}</li>
              <li>The Rs. {confirmRow.earned} reward transaction</li>
              <li>The ledger balance credit</li>
              <li>The weekly video assignment (user will be re-assigned)</li>
            </ul>
            <p style={{ margin: '0 0 22px', fontSize: 12, color: 'var(--awp-text-subtle)', textAlign: 'center' }}>
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmRow(null)}
                disabled={deleting}
                style={{
                  padding: '8px 24px', fontSize: 14, borderRadius: 8,
                  border: '1px solid var(--awp-border)', background: 'var(--awp-surface-2)',
                  color: 'var(--awp-text-muted)', cursor: 'pointer', fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '8px 22px', fontSize: 14, borderRadius: 8, border: 'none',
                  background: 'var(--awp-danger)', color: 'var(--awp-accent-contrast)', cursor: 'pointer', fontWeight: 600,
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAdWatchLog;
